/**
 * Email + password sign-in helpers the first sign-in screen needs (ADR-0051), pure so they are
 * unit-tested: the sign-in error text, the invitation link and the retry backoff after a failed
 * send. The code step, reset, invitation and settings helpers are in `passwordForms.ts`, which only
 * lazily loaded screens import (the web entry budget). Nothing here stores or logs a password.
 */

import type { ErrorLike } from "./pin";

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const CODE_LENGTH = 6;

export function asErrorLike(e: unknown): ErrorLike {
  return typeof e === "object" && e !== null ? e : {};
}

/** The token in a `#invite=<token>` URL fragment, or null. */
export function inviteTokenFromHash(hash: string): string | null {
  const m = /^#invite=([0-9a-fA-F-]{36}\.[A-Za-z0-9_-]{43})$/.exec(hash.trim());
  return m?.[1] ?? null;
}

/** Wait before the next try after `failures` failed sends (5 s, 15 s, 30 s, then 60 s). */
export function sendRetryDelayMs(failures: number): number {
  const steps = [5_000, 15_000, 30_000, 60_000];
  return steps[Math.min(Math.max(failures, 1), steps.length) - 1] ?? 60_000;
}

/** A failed send (503 EMAIL_SEND_FAILED, never a silent success) or a code asked for too soon. */
export function emailSendError(err: ErrorLike): string | null {
  if (err.code === "EMAIL_SEND_FAILED") {
    return "Could not send the code. Try again in a moment; if it keeps failing, TradePrizm's email settings need attention (or use your passkey or PIN).";
  }
  if (err.code === "CODE_TOO_SOON") return err.message ?? "A code was just sent. Wait a few seconds.";
  return null;
}

/** A readable message for a failed password sign-in. */
export function passwordSignInError(e: unknown): string {
  const err = asErrorLike(e);
  const send = emailSendError(err);
  if (send) return send;
  if (err.status === 429) {
    return "Too many sign-in attempts from this network or for this email. Wait up to an hour, then try again.";
  }
  if (err.code === "SIGNIN_REFUSED" || err.status === 401) {
    return "Email or password not accepted. After 5 wrong tries, sign-in is locked for a while (15 min, then longer).";
  }
  return err.message ?? "Sign-in failed.";
}
